require('./element.sass');
up.element = (function () {
    const u = up.util;
    function first(...args) {
        let [root = document, selector] = u.args(args, 'val', 'val');
        return root.querySelector(selector);
    }
    function subtree(root, selector) {
        const descendantMatches = root.querySelectorAll(selector);
        if (elementLikeMatches(root, selector)) {
            return [root, ...descendantMatches];
        }
        else {
            return descendantMatches;
        }
    }
    function subtreeFirst(root, selector) {
        return elementLikeMatches(root, selector) ? root : root.querySelector(selector);
    }
    function elementLikeMatches(elementLike, selector) {
        return u.isElement(elementLike) && elementLike.matches(selector);
    }
    function contains(root, selectorOrElement) {
        const element = getOne(selectorOrElement);
        return Node.prototype.contains.call(root, element);
    }
    function ancestor(element, selector) {
        return element.parentNode?.closest(selector);
    }
    function around(element, selector) {
        return getList(element.closest(selector), subtree(element, selector));
    }
    function getOne(...args) {
        const value = args.pop();
        if (u.isElement(value)) {
            return value;
        }
        else if (u.isString(value)) {
            return first(...args, value);
        }
        else if (u.isList(value)) {
            if (value.length > 1) {
                up.fail('up.element.get(): Cannot cast multiple elements (%o) to a single element', value);
            }
            return value[0];
        }
        else {
            return value;
        }
    }
    function getList(...args) {
        return u.flatMap(args, valueToList);
    }
    function valueToList(value) {
        if (u.isString(value)) {
            return document.querySelectorAll(value);
        }
        else {
            return u.wrapList(value);
        }
    }
    function hide(element) {
        setVisible(element, false);
    }
    function show(element) {
        setVisible(element, true);
    }
    function showTemp(element) {
        return setVisibleTemp(element, true);
    }
    function hideTemp(element) {
        return setVisibleTemp(element, false);
    }
    function setVisibleTemp(element, newVisible) {
        if (newVisible === isVisible(element))
            return u.noop;
        setVisible(element, newVisible);
        return () => setVisible(element, !newVisible);
    }
    function setVisible(element, newVisible) {
        if (newVisible) {
            element.removeAttribute('hidden');
            if (element.style.display === 'none') {
                element.style.display = '';
            }
        }
        else {
            element.setAttribute('hidden', '');
        }
    }
    function toggle(element, newVisible = !isVisible(element)) {
        setVisible(element, newVisible);
    }
    function setAttrPresence(element, attr, value, newPresent) {
        if (newPresent) {
            return element.setAttribute(attr, value);
        }
        else {
            return element.removeAttribute(attr);
        }
    }
    function setAttrs(element, attrs) {
        for (let key in attrs) {
            const value = attrs[key];
            if (u.isGiven(value)) {
                element.setAttribute(key, value);
            }
            else {
                element.removeAttribute(key);
            }
        }
    }
    function setAttrsTemp(element, attrs) {
        let keys = Object.keys(attrs);
        let oldAttrs = pickAttrs(element, keys);
        setAttrs(element, attrs);
        return () => setAttrs(element, oldAttrs);
    }
    function metaContent(name) {
        const selector = "meta" + attrSelector('name', name);
        return document.head.querySelector(selector)?.content;
    }
    function insertBefore(existingNode, newNode) {
        existingNode.parentNode.insertBefore(newNode, existingNode);
    }
    function createFromSelector(selector, attrs = {}) {
        let { includePath } = parseSelector(selector);
        let rootElement;
        let depthElement;
        let previousElement;
        for (let includeSegment of includePath) {
            let { tagName } = includeSegment;
            if (!tagName || tagName === '*') {
                tagName = 'div';
            }
            depthElement = document.createElement(tagName);
            if (!rootElement) {
                rootElement = depthElement;
            }
            makeVariation(depthElement, includeSegment);
            previousElement?.appendChild(depthElement);
            previousElement = depthElement;
        }
        for (let key in attrs) {
            let value = attrs[key];
            if (key === 'class') {
                addClasses(rootElement, u.wrapList(value));
            }
            else if (key === 'style') {
                setInlineStyle(rootElement, value);
            }
            else if (key === 'text') {
                rootElement.textContent = value;
            }
            else if (key === 'content') {
                if (u.isString(value)) {
                    rootElement.innerHTML = value;
                }
                else {
                    rootElement.append(...u.wrapList(value));
                }
            }
            else {
                rootElement.setAttribute(key, value);
            }
        }
        return rootElement;
    }
    function makeVariation(element, { id, classNames, attributes }) {
        if (id) {
            element.id = id;
        }
        for (let [name, value] of Object.entries(attributes)) {
            element.setAttribute(name, value);
        }
        addClasses(element, classNames);
    }
    function parseSelector(rawSelector) {
        let excludeRaw;
        const { masked: selectorOutline, restore: restoreSelectorLiterals, } = u.expressionOutline(rawSelector);
        const includeWithoutAttrs = selectorOutline.replace(/:not\([^)]*\)/, function (match) {
            excludeRaw = restoreSelectorLiterals(match);
            return '';
        });
        let includeRaw = restoreSelectorLiterals(includeWithoutAttrs);
        const includeSegments = includeWithoutAttrs.split(/[ >]+/);
        let includePath = includeSegments.map(function (depthSelector) {
            let parsed = {
                tagName: null,
                classNames: [],
                id: null,
                attributes: {}
            };
            depthSelector = depthSelector.replace(/^[\w-*]+/, function (match) {
                parsed.tagName = match;
                return '';
            });
            depthSelector = depthSelector.replace(/#([\w-]+)/, function (_match, id) {
                parsed.id = id;
                return '';
            });
            depthSelector = depthSelector.replace(/\.([\w-]+)/g, function (_match, className) {
                parsed.classNames.push(className);
                return '';
            });
            depthSelector = depthSelector.replace(/\[[^\]]*]/g, function (attr) {
                attr = restoreSelectorLiterals(attr);
                let [_raw, name, _operator, quote, value] = attr.match(/\[([\w-]+)(?:([~|^$*]?=)(["'])?([^\3\]]*?)\3)?]/);
                quote ||= '"';
                parsed.attributes[name] = value ? u.parseString(quote + value + quote) : '';
                return '';
            });
            if (depthSelector) {
                up.fail('Cannot parse selector: ' + rawSelector);
            }
            return parsed;
        });
        return {
            includePath,
            includeRaw,
            excludeRaw,
        };
    }
    function affix(...args) {
        let [parent, position = 'beforeend', selector, attributes] = u.args(args, 'val', u.isAdjacentPosition, 'val', 'options');
        const element = createFromSelector(selector, attributes);
        parent.insertAdjacentElement(position, element);
        return element;
    }
    const SINGLETON_TAG_NAMES = ['HTML', 'BODY', 'HEAD', 'TITLE'];
    const isSingleton = up.mockable((element) => element.matches(SINGLETON_TAG_NAMES.join()));
    function elementTagName(element) {
        return element.tagName.toLowerCase();
    }
    function attrSelector(attribute, value) {
        if (u.isGiven(value)) {
            value = value.replace(/"/g, '\\"');
            return `[${attribute}="${value}"]`;
        }
        else {
            return `[${attribute}]`;
        }
    }
    function idSelector(id) {
        if (id.match(/^[a-z][a-z0-9\-_]*$/i)) {
            return `#${id}`;
        }
        else {
            return attrSelector('id', id);
        }
    }
    function classSelector(klass) {
        klass = klass.replace(/[^\w-]/g, '\\$&');
        return `.${klass}`;
    }
    function createBrokenDocumentFromHTML(html) {
        const firstTag = firstTagNameInHTML(html);
        if (['TR', 'TD', 'TH', 'THEAD', 'TBODY'].includes(firstTag)) {
            html = `<table>${html}</table>`;
        }
        return new DOMParser().parseFromString(html, 'text/html');
    }
    function revivedClone(element) {
        let clone = createFromHTML(element.outerHTML);
        if ('nonce' in element)
            clone.nonce = element.nonce;
        return clone;
    }
    function createFromHTML(html) {
        return extractSingular(createNodesFromHTML(html));
    }
    function extractSingular(nodes) {
        return tryExtractSingular(nodes) || up.fail('Expected a single element, but got %d nodes', nodes.length);
    }
    function tryExtractSingular(nodes) {
        if (nodes.length === 1 && u.isElementLike(nodes[0])) {
            return nodes[0];
        }
    }
    function createNodesFromHTML(html) {
        html = html.trim();
        const range = document.createRange();
        range.setStart(document.body, 0);
        const fragment = range.createContextualFragment(html);
        return fragment.childNodes;
    }
    function getRoot() {
        return document.documentElement;
    }
    function paint(element) {
        element.offsetHeight;
    }
    function fixedToAbsolute(element) {
        const elementRectAsFixed = element.getBoundingClientRect();
        element.style.position = 'absolute';
        const offsetParentRect = element.offsetParent.getBoundingClientRect();
        setInlineStyle(element, {
            left: (elementRectAsFixed.left - computedStyleNumber(element, 'margin-left') - offsetParentRect.left) + 'px',
            top: (elementRectAsFixed.top - computedStyleNumber(element, 'margin-top') - offsetParentRect.top) + 'px',
            right: '',
            bottom: ''
        });
    }
    function setMissingAttrs(element, attrs) {
        for (let key in attrs) {
            setMissingAttr(element, key, attrs[key]);
        }
    }
    function setMissingAttr(element, key, value) {
        if (u.isMissing(element.getAttribute(key))) {
            element.setAttribute(key, value);
        }
    }
    function unwrap(wrapper) {
        preservingFocus(function () {
            let childNodes = [...wrapper.childNodes];
            for (let child of childNodes)
                insertBefore(wrapper, child);
            wrapper.remove();
        });
    }
    function wrapNodes(nodeOrNodes) {
        const wrapper = document.createElement('up-wrapper');
        wrapper.append(...u.wrapList(nodeOrNodes));
        return wrapper;
    }
    function wrapIfRequired(nodes) {
        if (nodes.length === 1 && u.isElement(nodes[0])) {
            return nodes[0];
        }
        else {
            return wrapNodes(nodes);
        }
    }
    function wrapChildren(element) {
        const wrapper = wrapNodes(element.childNodes);
        element.append(wrapper);
        return wrapper;
    }
    function preservingFocus(fn) {
        const oldFocusElement = document.activeElement;
        try {
            return fn();
        }
        finally {
            if (oldFocusElement && oldFocusElement !== document.activeElement) {
                oldFocusElement.focus({ preventScroll: true });
            }
        }
    }
    function parseAttr(element, attribute, ...parsers) {
        if (!element.hasAttribute?.(attribute))
            return undefined;
        let rawValue = element.getAttribute(attribute);
        for (let parser of parsers) {
            let parserResult = parser(rawValue, attribute, element);
            if (u.isDefined(parserResult))
                return parserResult;
        }
    }
    function stringAttr(element, attribute) {
        return parseAttr(element, attribute, tryParseString);
    }
    let tryParseString = u.identity;
    function booleanAttr(element, attribute) {
        return parseAttr(element, attribute, tryParseBoolean);
    }
    function tryParseBoolean(value, attribute) {
        switch (value) {
            case 'false': {
                return false;
            }
            case 'true':
            case '':
            case attribute:
                {
                    return true;
                }
        }
    }
    function booleanOrStringAttr(element, attribute) {
        return parseAttr(element, attribute, tryParseBoolean, tryParseString);
    }
    function booleanOrNumberAttr(element, attribute) {
        return parseAttr(element, attribute, tryParseBoolean, tryParseNumber);
    }
    function booleanOrNumberOrStringAttr(element, attribute) {
        return parseAttr(element, attribute, tryParseBoolean, tryParseNumber, tryParseString);
    }
    function numberAttr(element, attribute) {
        return parseAttr(element, attribute, tryParseNumber);
    }
    function tryParseNumber(value) {
        return u.parseNumber(value);
    }
    function jsonAttr(element, attribute) {
        return parseAttr(element, attribute, tryParseJSON);
    }
    function tryParseJSON(value) {
        if (value?.trim()) {
            return u.parseRelaxedJSON(value);
        }
    }
    function closestAttr(element, attr, readAttrFn = stringAttr) {
        let match = element.closest('[' + attr + ']');
        if (match) {
            return readAttrFn(match, attr);
        }
    }
    function addClasses(element, classes) {
        for (let klass of classes)
            element.classList.add(klass);
    }
    function addClassTemp(element, klass) {
        return setClassStateTemp(element, klass, true);
    }
    function removeClassTemp(element, klass) {
        return setClassStateTemp(element, klass, false);
    }
    function setClassStateTemp(element, klass, targetState) {
        if (element.classList.contains(klass) === targetState)
            return u.noop;
        element.classList.toggle(klass, targetState);
        return () => element.classList.toggle(klass, !targetState);
    }
    function computedStyle(element, props) {
        const style = window.getComputedStyle(element);
        return extractFromStyleObject(style, props);
    }
    function computedStyleNumber(element, prop) {
        const rawValue = computedStyle(element, prop);
        if (u.isPresent(rawValue)) {
            return parseFloat(rawValue);
        }
    }
    function inlineStyle(element, props) {
        const { style } = element;
        return extractFromStyleObject(style, props);
    }
    function extractFromStyleObject(style, keyOrKeys) {
        if (up.migrate.loaded)
            keyOrKeys = up.migrate.fixGetStyleProps(keyOrKeys);
        if (u.isString(keyOrKeys)) {
            return style.getPropertyValue(keyOrKeys);
        }
        else {
            return u.mapObject(keyOrKeys, (key) => [key, style.getPropertyValue(key)]);
        }
    }
    function setInlineStyle(element, props, unit = '') {
        if (up.migrate.loaded)
            props = up.migrate.fixSetStyleProps(props, unit);
        if (u.isString(props)) {
            element.setAttribute('style', props);
        }
        else {
            const { style } = element;
            for (let key in props) {
                let value = props[key];
                style.setProperty(key, value + unit);
            }
        }
    }
    function setStyleTemp(element, newStyles) {
        const oldStyles = inlineStyle(element, Object.keys(newStyles));
        setInlineStyle(element, newStyles);
        return () => setInlineStyle(element, oldStyles);
    }
    function isVisible(element) {
        return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
    }
    function isUpPrefixed(string) {
        return /^up-/.test(string);
    }
    function pickAttrs(element, attrNames) {
        return u.mapObject(attrNames, (name) => [name, element.getAttribute(name)]);
    }
    function upAttrs(element) {
        let attrNames = element.getAttributeNames().filter(isUpPrefixed);
        return pickAttrs(element, attrNames);
    }
    function upClasses(element) {
        return u.filter(element.classList.values(), isUpPrefixed);
    }
    function isEmpty(element) {
        return !element.children.length > 0 && !element.innerText.trim();
    }
    function crossOriginSelector(attr) {
        return `[${attr}*="//"]:not([${attr}*="//${location.host}/"])`;
    }
    function isIntersectingWindow(element, { margin = 0 } = {}) {
        const rect = up.Rect.fromElement(element);
        rect.grow(margin);
        return (rect.bottom > 0) && (rect.top < window.innerHeight) &&
            (rect.right > 0) && (rect.left < window.innerWidth);
    }
    function unionSelector(includes, excludes, condition) {
        let selector = `:is(${includes.join()})`;
        if (u.isPresent(excludes))
            selector += `:not(${excludes.join()})`;
        if (condition)
            selector += `:is(${condition})`;
        return selector;
    }
    function documentPosition(element) {
        let nextSibling = element.nextElementSibling;
        if (nextSibling) {
            return [nextSibling, 'beforebegin'];
        }
        else {
            return [element.parentElement, 'beforeend'];
        }
    }
    const FIRST_TAG_NAME_PATTERN = /^\s*(<!--[^-]*.*?-->\s*)*<([a-z-!]+)\b/i;
    function firstTagNameInHTML(html) {
        return FIRST_TAG_NAME_PATTERN.exec(html)?.[2].toUpperCase();
    }
    function isFullDocumentHTML(html) {
        let firstTag = firstTagNameInHTML(html);
        return firstTag === 'HTML' || firstTag === '!DOCTYPE';
    }
    function leadingElement(nodes) {
        for (let node of nodes) {
            if (u.isElement(node)) {
                return node;
            }
            else if (u.isTextNode(node) && node.textContent.trim()) {
                return;
            }
            else {
            }
        }
    }
    function removeAttrsFromHTML(html, attrs) {
        let tagPattern = /<[^<]+>/g;
        let attrPattern = new RegExp(`\\s+(${attrs.join('|')})="[^"]*"`, 'g');
        let cleanTag = (match) => match.replace(attrPattern, '');
        html = html.replace(tagPattern, cleanTag);
        return html;
    }
    return {
        subtree,
        subtreeFirst,
        contains,
        closestAttr,
        ancestor,
        around,
        get: getOne,
        list: getList,
        toggle,
        hide,
        hideTemp,
        show,
        showTemp,
        metaContent,
        insertBefore,
        createFromSelector,
        setAttrs,
        setAttrsTemp,
        affix,
        idSelector,
        classSelector,
        isSingleton,
        attrSelector,
        tagName: elementTagName,
        createBrokenDocumentFromHTML,
        revivedClone,
        createNodesFromHTML,
        createFromHTML,
        extractSingular,
        get root() { return getRoot(); },
        paint,
        fixedToAbsolute,
        setMissingAttrs,
        setMissingAttr,
        unwrap,
        wrapChildren,
        wrapIfRequired,
        attr: stringAttr,
        booleanAttr,
        numberAttr,
        jsonAttr,
        parseAttr,
        booleanOrStringAttr,
        booleanOrNumberAttr,
        booleanOrNumberOrStringAttr,
        setStyleTemp,
        style: computedStyle,
        styleNumber: computedStyleNumber,
        inlineStyle,
        setStyle: setInlineStyle,
        isVisible,
        upAttrs,
        upClasses,
        setAttrPresence,
        addClasses,
        addClassTemp,
        removeClassTemp,
        parseSelector,
        isEmpty,
        crossOriginSelector,
        isIntersectingWindow,
        unionSelector,
        elementLikeMatches,
        documentPosition,
        isFullDocumentHTML,
        leadingElement,
        removeAttrsFromHTML,
    };
})();
